// ============================================================================
// SchematicDiagram.jsx
// Hybrid block diagram generator for IoT hardware circuits.
// Renders an SVG schematic and exports it as a PDF.
// ============================================================================
import React, { useRef, useEffect, useState, useCallback } from 'react';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';
import './schematicDiagram.css';

// ─── Constants ───────────────────────────────────────────────────────────────

const HARDWARE_CATEGORIES = [
  'boards', 'sensors', 'actuators', 'modules',
  'displays', 'components', 'power', 'plc'
];

// Wire colour rules — matched by wireType first, then pin name pattern
const WIRE_COLORS = {
  power:   { color: '#ef4444', dash: false, label: 'Power (VCC)'    },
  ground:  { color: '#1f2937', dash: false, label: 'Ground (GND)'   },
  i2c:     { color: '#3b82f6', dash: false, label: 'I2C'            },
  spi:     { color: '#8b5cf6', dash: false, label: 'SPI'            },
  uart:    { color: '#f59e0b', dash: false, label: 'UART'           },
  digital: { color: '#10b981', dash: true,  label: 'Digital Signal' },
  analog:  { color: '#14b8a6', dash: false, label: 'Analog Signal'  },
  default: { color: '#64748b', dash: false, label: 'Signal'         },
};

// Box dimensions
const BOX_W       = 140;
const BOX_H       = 110;
const PIN_H       = 24;
const H_GAP       = 80;   // horizontal gap between columns
const V_GAP       = 30;   // vertical gap between rows
const CANVAS_PAD  = 60;   // padding around the whole diagram

// Distinct colors for data signals to avoid overlap confusion
const DATA_COLORS = [
  '#10b981', // green
  '#3b82f6', // blue
  '#8b5cf6', // purple
  '#f59e0b', // amber
  '#ef4444', // red
  '#14b8a6', // teal
  '#f97316', // orange
  '#06b6d4', // cyan
  '#84cc16', // lime
  '#ec4899', // pink
  '#6366f1', // indigo
  '#a855f7', // violet
];

// Picks a color from DATA_COLORS based on connection index
function resolveWireStyleWithIndex(conn, index) {
  const pins = `${conn.from.pinName} ${conn.to.pinName}`.toLowerCase();

  // Power and ground always keep fixed colors
  if (pins.includes('vcc') || pins.includes('3.3') || pins.includes('5v') || pins.includes('power'))
    return WIRE_COLORS.power;
  if (pins.includes('gnd') || pins.includes('ground'))
    return WIRE_COLORS.ground;

  // All other wires — use index for distinct colors
  // This means SCK, MOSI, MISO, CS, SDA, SCL, TX, RX, GPIO etc.
  // all get unique colors so they can be distinguished
  return {
    color: DATA_COLORS[index % DATA_COLORS.length],
    dash: false,
    label: conn.from.pinName,
  };
}

// ─── Layout engine ────────────────────────────────────────────────────────────

/**
 * Groups hardware components into layout columns:
 *   left  → sensors, modules
 *   center→ boards, power
 *   right → actuators, displays, components
 *
 * Returns an array of placed components with { ...comp, sx, sy }
 * where sx/sy are the top-left corner of the component box in SVG space,
 * and the total { svgW, svgH } of the diagram.
 */
function computeLayout(components) {
  // Show ALL components regardless of category — not just hardware.
  const hw = components;

  const board  = hw.filter(c => c.category === 'boards');
  const left   = hw.filter(c => ['sensors', 'modules'].includes(c.category));
  const bottom = hw.filter(c => c.category === 'power');
  // Catch-all: anything not explicitly bucketed above (AgenticAI graph nodes,
  // foundation/chain/rag/tool components, IIoT PLC blocks, etc.) lands in the
  // right column — same slot the old 'actuators/displays/components/plc' group used.
  const right  = hw.filter(c =>
    c.category !== 'boards' &&
    !['sensors', 'modules'].includes(c.category) &&
    c.category !== 'power'
  );

  const compH = (comp) => BOX_H + comp.pinDetails.filter(p => p.visible !== false).length * PIN_H;

  // Column heights
  const colH = (group) => group.reduce((acc, c) => acc + compH(c) + V_GAP, 0);

  const leftH   = colH(left);
  const boardH  = colH(board);
  const rightH  = colH(right);
  const centerH = Math.max(leftH, boardH, rightH);

  const totalW = CANVAS_PAD * 2 + BOX_W + H_GAP + BOX_W + H_GAP + BOX_W;

  // X positions of each column
  const leftX   = CANVAS_PAD;
  const centerX = CANVAS_PAD + BOX_W + H_GAP;
  const rightX  = CANVAS_PAD + BOX_W + H_GAP + BOX_W + H_GAP;

  const placed = [];

  const placeCol = (group, x, totalColH) => {
    const startY = CANVAS_PAD + (centerH - totalColH) / 2;
    let y = startY;
    group.forEach(comp => {
      placed.push({ ...comp, sx: x, sy: y });
      y += compH(comp) + V_GAP;
    });
  };

  placeCol(left,   leftX,   leftH);
  placeCol(board,  centerX, boardH);
  placeCol(right,  rightX,  rightH);

  // Power row below everything
  let px = CANVAS_PAD;
  const powerY = CANVAS_PAD + centerH + V_GAP * 2;
  bottom.forEach(comp => {
    placed.push({ ...comp, sx: px, sy: powerY });
    px += BOX_W + H_GAP;
  });

  const bottomRowH = bottom.length > 0
    ? compH(bottom[0]) + V_GAP
    : 0;

  const svgW = Math.max(totalW, px);
  const svgH = powerY + bottomRowH + CANVAS_PAD;

  return { placed, svgW, svgH };
}

// ─── Pin anchor calculator ────────────────────────────────────────────────────

/**
 * Returns { x, y } anchor point for a pin on a placed component box.
 * Left-column components use right-side anchors,
 * right-column components use left-side anchors,
 * center (board) uses both sides depending on which component connects.
 */
function getPinAnchor(placedComp, pinName, side = 'right') {
  const visiblePins = (placedComp.pinDetails || [])
    .filter(p => p.visible !== false)
    .map(p => p.pinName);

  const pinIndex = visiblePins.indexOf(pinName);
  const pinY = placedComp.sy + BOX_H + (pinIndex >= 0 ? pinIndex * PIN_H + PIN_H / 2 : BOX_H / 2);
  const pinX = side === 'right'
    ? placedComp.sx + BOX_W
    : placedComp.sx;

  return { x: pinX, y: pinY };
}

// ─── SVG Component Box ────────────────────────────────────────────────────────

function ComponentBox({ comp }) {
  const color      = comp.visual?.color || '#94a3b8';
  const imageUrl   = comp.visual?.imageUrl || null;
  const visiblePins = (comp.pinDetails || []).filter(p => p.visible !== false);
  const totalH     = BOX_H + visiblePins.length * PIN_H;

  return (
    <g key={comp.instanceId} transform={`translate(${comp.sx}, ${comp.sy})`}>
      {/* Box shadow */}
      <rect
        x="3" y="3"
        width={BOX_W} height={totalH}
        rx="8" ry="8"
        fill="rgba(0,0,0,0.08)"
      />

      {/* Main box */}
      <rect
        x="0" y="0"
        width={BOX_W} height={totalH}
        rx="8" ry="8"
        fill={`${color}22`}
        stroke={color}
        strokeWidth="2"
      />

      {/* Header band */}
      <rect
        x="0" y="0"
        width={BOX_W} height="36"
        rx="8" ry="8"
        fill={color}
      />
      <rect x="0" y="20" width={BOX_W} height="16" fill={color} />

      {/* Icon or image */}
      {imageUrl ? (
        <image
          href={imageUrl}
          x="8" y="40"
          width="40" height="40"
          preserveAspectRatio="xMidYMid meet"
        />
      ) : (
        <text
          x={imageUrl ? BOX_W / 2 + 20 : BOX_W / 2}
          y="72"
          textAnchor="middle"
          fontSize="28"
          dominantBaseline="middle"
        >
          {comp.icon || '📦'}
        </text>
      )}

      {/* Component label in header */}
      <text
        x={BOX_W / 2} y="22"
        textAnchor="middle"
        fontSize="11"
        fontWeight="700"
        fill="#ffffff"
        dominantBaseline="middle"
      >
        {(comp.label || comp.componentName || comp.name || '').slice(0, 18)}
      </text>

      {/* Instance label below image */}
      <text
        x={BOX_W / 2} y={BOX_H - 10}
        textAnchor="middle"
        fontSize="9"
        fill="#475569"
      >
        {comp.componentName || comp.name || ''}
      </text>

      {/* Pin rows */}
      {/* {visiblePins.map((pin, idx) => (
        <g key={pin.pinName} transform={`translate(0, ${BOX_H + idx * PIN_H})`}>
          <rect
            x="0" y="0"
            width={BOX_W} height={PIN_H}
            fill={idx % 2 === 0 ? 'rgba(0,0,0,0.03)' : 'transparent'}
            stroke={color}
            strokeWidth="0.5"
            strokeOpacity="0.3"
          />
          
          {!pin.connectedTo && (
            <text
              x="8" y={PIN_H / 2}
              fontSize="9"
              fill="#334155"
              dominantBaseline="middle"
            >
              {pin.pinName}
            </text>
          )}
          {pin.connectedTo && (
            <circle cx={BOX_W - 8} cy={PIN_H / 2} r="3" fill={color} />
          )}
        </g>
      ))} */}
    </g>
  );
}

function resolveSettingValue(value) {
  if (value === null || value === undefined) return '—';
  if (typeof value === 'boolean')  return value ? '✅ Yes' : '❌ No';
  if (typeof value === 'object')   return JSON.stringify(value);
  if (String(value).trim() === '') return '—';
  return String(value);
}

// ─── Configuration Page ───────────────────────────────────────────────────────
const ConfigPage = ({ components, logoSrc, courseTitle, labTitle,
                      circuitName, studentName, circuitId, svgW }) => {

  // Filter: only needsConfiguration === true, hardware canvas only
  const configComponents = components.filter(c =>
    // HARDWARE_CATEGORIES.includes(c.category) &&
    c.needsConfiguration === true
  );

  // ADD THIS TEMPORARILY:
  console.log('🔍 Config components:', configComponents.map(c => ({
    name: c.name,
    label: c.label,
    communicationConfigs: c.communicationConfigs,
    wirelessConfigs: c.wirelessConfigs,
  })));

  const date = new Date().toLocaleDateString('en-CA');

  return (
    <div style={{
      width: svgW,
      background: '#ffffff',
      fontFamily: "'Segoe UI', system-ui, sans-serif",
    }}>

      {/* ── Title block — identical style to SVG title block ── */}
      <div style={{
        background: '#1e293b',
        borderBottom: '3px solid #3b82f6',
        padding: '10px 16px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: '12px',
      }}>
        {/* Left — logo + brand */}
        <div style={{ display: 'flex', alignItems: 'center', gap: '10px', minWidth: 180 }}>
          {logoSrc && (
            <img
              src={logoSrc}
              alt="ProfiX Labs"
              style={{ width: 44, height: 44, objectFit: 'contain' }}
            />
          )}
          <div>
            <div style={{ color: '#f1f5f9', fontWeight: 800, fontSize: 15, letterSpacing: 0.3 }}>
              ProfiX Labs
            </div>
            <div style={{ color: '#94a3b8', fontSize: 8, letterSpacing: 1, textTransform: 'uppercase' }}>
              Component Configuration
            </div>
          </div>
        </div>

        {/* Center — circuit / course / lab */}
        <div style={{ textAlign: 'center', flex: 1 }}>
          <div style={{ color: '#f1f5f9', fontWeight: 700, fontSize: 13 }}>
            {circuitName || 'Circuit Schematic'}
          </div>
          <div style={{ color: '#94a3b8', fontSize: 10, marginTop: 2 }}>
            {courseTitle || ''}
          </div>
          <div style={{ color: '#64748b', fontSize: 9, marginTop: 1 }}>
            {labTitle || ''}
          </div>
        </div>

        {/* Right — student / date / ID */}
        <div style={{ textAlign: 'right', minWidth: 180 }}>
          <div style={{ color: '#94a3b8', fontSize: 10 }}>{studentName || ''}</div>
          <div style={{ color: '#64748b', fontSize: 9, marginTop: 2 }}>{date}</div>
          <div style={{ color: '#475569', fontSize: 8, marginTop: 2, fontFamily: 'monospace' }}>
            {circuitId || ''}
          </div>
        </div>
      </div>

      {/* ── Page heading ── */}
      <div style={{
        padding: '16px 24px 8px',
        borderBottom: '1px solid #e2e8f0',
      }}>
        <div style={{ fontSize: 16, fontWeight: 700, color: '#1e293b' }}>
          ⚙️ Component Configuration
        </div>
        <div style={{ fontSize: 11, color: '#64748b', marginTop: 3 }}>
          Showing {configComponents.length} configured component{configComponents.length !== 1 ? 's' : ''}
        </div>
      </div>

      {/* ── Component config blocks ── */}
      <div style={{ padding: '16px 24px', display: 'flex', flexDirection: 'column', gap: 24 }}>

        {configComponents.length === 0 && (
          <div style={{ color: '#94a3b8', fontSize: 13, textAlign: 'center', padding: '40px 0' }}>
            No components require configuration in this circuit.
          </div>
        )}

        {configComponents.map(comp => {
          const color = comp.visual?.color || '#94a3b8';

          // Only enabled communication protocols
          const commEntries = Object.entries(comp.communicationConfigs || {})
            .filter(([, cfg]) => cfg?.enabled === true);
          const hasComm = commEntries.length > 0;

          // Only enabled wireless types
          const wirelessEntries = Object.entries(comp.wirelessConfigs || {})
            .filter(([, cfg]) => cfg?.enabled === true);
          const hasWireless = wirelessEntries.length > 0;

          // Only enabled software pins
          const softPins = (comp.pinDetails || [])
            .filter(p => p.isSoftwarePin === true && p.enabled === true);
          const hasPins = softPins.length > 0;

          return (
            <div key={comp.instanceId} style={{
              border: `2px solid ${color}44`,
              borderRadius: 10,
              overflow: 'hidden',
            }}>

              {/* Component header */}
              <div style={{
                background: color,
                padding: '8px 14px',
                display: 'flex',
                alignItems: 'center',
                gap: 10,
              }}>
                <span style={{ fontSize: 20 }}>{comp.icon || '📦'}</span>
                <div>
                  <div style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
                    {comp.label || comp.componentName || comp.name}
                  </div>
                  <div style={{ color: 'rgba(255,255,255,0.7)', fontSize: 10 }}>
                    {comp.componentName || comp.name} · {comp.instanceId}
                  </div>
                </div>
              </div>

              <div style={{ padding: '12px 14px', display: 'flex', flexDirection: 'column', gap: 14 }}>

                {/* ── Section A: Communication Configs ── */}
                {hasComm && commEntries.map(([protocolKey, protocolCfg]) => {
                  const settings    = protocolCfg?.settings || {};
                  const settingKeys = Object.keys(settings);

                  return (
                    <div key={protocolKey}>
                      {/* Protocol header row */}
                      <div style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 8,
                        marginBottom: 6,
                      }}>

                        <div style={{
                          background: '#dcfce7',
                          color: '#166534',
                          fontSize: 9,
                          fontWeight: 700,
                          padding: '2px 7px',
                          borderRadius: 4,
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}>
                          Enabled
                        </div>

                        <div style={{
                          fontSize: 11,
                          fontWeight: 700,
                          color: '#1e293b',
                          textTransform: 'uppercase',
                          letterSpacing: 0.5,
                        }}>
                          {protocolKey}
                        </div>
                      </div>

                      {/* Settings table */}
                      <table style={{
                        width: '100%',
                        borderCollapse: 'collapse',
                        fontSize: 11,
                      }}>
                        <thead>
                          <tr style={{ background: '#f1f5f9' }}>
                            <th style={thStyle}>Setting</th>
                            <th style={thStyle}>Value</th>
                          </tr>
                        </thead>
                        <tbody>
                          {settingKeys.length === 0 ? (
                            <tr>
                              <td colSpan={2} style={{ ...tdStyle, color: '#94a3b8', fontStyle: 'italic' }}>
                                No settings configured
                              </td>
                            </tr>
                          ) : (
                            settingKeys.map((key, i) => (
                              <tr key={key} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                <td style={{ ...tdStyle, color: '#475569', fontFamily: 'monospace', width: '40%' }}>
                                  {key}
                                </td>
                                <td style={{ ...tdStyle, color: '#1e293b', fontWeight: 500 }}>
                                  {resolveSettingValue(settings[key])}
                                </td>
                              </tr>
                            ))
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })}


                {/* ── Section B: Wireless Configs ── */}
                {hasWireless && (() => {
                  return (
                  <div>
                    {hasComm && <div style={{ borderTop: '1px dashed #e2e8f0', marginBottom: 14 }} />}
                    <div style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#1e293b',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 6,
                    }}>
                      Wireless Configuration
                    </div>

                    {wirelessEntries.map(([wirelessKey, wirelessCfg]) => {
                      const settings    = wirelessCfg?.settings || {};
                      const settingKeys = Object.keys(settings);

                      return (
                        <div key={wirelessKey} style={{ marginBottom: 12 }}>
                          {/* Wireless type header */}
                          <div style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            marginBottom: 6,
                          }}>

                            <div style={{
                              background: '#dcfce7',
                              color: '#166534',
                              fontSize: 9,
                              fontWeight: 700,
                              padding: '2px 7px',
                              borderRadius: 4,
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}>
                              Enabled
                            </div>

                            <div style={{
                              fontSize: 11,
                              fontWeight: 700,
                              color: '#1e293b',
                              textTransform: 'uppercase',
                              letterSpacing: 0.5,
                            }}>
                              {wirelessKey}
                            </div>
                          </div>

                          {/* Settings table */}
                          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                            <thead>
                              <tr style={{ background: '#f1f5f9' }}>
                                <th style={thStyle}>Setting</th>
                                <th style={thStyle}>Value</th>
                              </tr>
                            </thead>
                            <tbody>
                              {settingKeys.length === 0 ? (
                                <tr>
                                  <td colSpan={2} style={{ ...tdStyle, color: '#94a3b8', fontStyle: 'italic' }}>
                                    No settings configured
                                  </td>
                                </tr>
                              ) : (
                                settingKeys.map((key, i) => (
                                  <tr key={key} style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}>
                                    <td style={{ ...tdStyle, color: '#475569', fontFamily: 'monospace', width: '40%' }}>
                                      {key}
                                    </td>
                                    <td style={{ ...tdStyle, color: '#1e293b', fontWeight: 500 }}>
                                      {resolveSettingValue(settings[key])}
                                    </td>
                                  </tr>
                                ))
                              )}
                            </tbody>
                          </table>
                        </div>
                      );
                    })}
                  </div>
                );
                })()}

                {/* ── Divider before pins ── */}
                {(hasComm || hasWireless) && hasPins && (
                  <div style={{ borderTop: '1px dashed #e2e8f0' }} />
                )}

                {/* ── Section B: Software Pin Configuration ── */}
                {hasPins && (
                  <div>
                    <div style={{
                      fontSize: 11,
                      fontWeight: 700,
                      color: '#1e293b',
                      textTransform: 'uppercase',
                      letterSpacing: 0.5,
                      marginBottom: 6,
                    }}>
                      Software Pins
                    </div>

                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                      <thead>
                        <tr style={{ background: '#f1f5f9' }}>
                          <th style={thStyle}>Pin</th>
                          <th style={thStyle}>Type</th>
                          <th style={thStyle}>Direction</th>
                          <th style={thStyle}>Enabled</th>
                          <th style={thStyle}>Settings</th>
                        </tr>
                      </thead>
                      <tbody>
                        {softPins.map((pin, i) => {
                          const pinSettings    = pin.settings    || {};
                          const pinSettingKeys = Object.keys(pinSettings);

                          return (
                            <tr key={pin.pinName}
                              style={{ background: i % 2 === 0 ? '#ffffff' : '#f8fafc' }}
                            >
                              {/* Pin name */}
                              <td style={{
                                ...tdStyle,
                                fontFamily: 'monospace',
                                fontWeight: 700,
                                color: color,
                                width: '14%',
                              }}>
                                {pin.pinName}
                              </td>

                              {/* Software pin type */}
                              <td style={{ ...tdStyle, color: '#475569', width: '16%' }}>
                                {pin.softwarePinType || '—'}
                              </td>

                              {/* Direction */}
                              <td style={{ ...tdStyle, color: '#475569', width: '12%' }}>
                                {pin.direction
                                  ? pin.direction === 'input'
                                    ? '⬇ Input'
                                    : '⬆ Output'
                                  : '—'}
                              </td>

                              {/* Enabled badge */}
                              <td style={{ ...tdStyle, width: '12%' }}>
                                <span style={{
                                  background: pin.enabled ? '#dcfce7' : '#fee2e2',
                                  color:      pin.enabled ? '#166534' : '#991b1b',
                                  fontSize: 9,
                                  fontWeight: 700,
                                  padding: '2px 6px',
                                  borderRadius: 4,
                                }}>
                                  {pin.enabled ? 'ON' : 'OFF'}
                                </span>
                              </td>

                              {/* Settings — flattened key: value pairs */}
                              <td style={{ ...tdStyle, color: '#334155' }}>
                                {pinSettingKeys.length === 0
                                  ? <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>—</span>
                                  : pinSettingKeys.map(k => (
                                    <div key={k} style={{ fontSize: 10 }}>
                                      <span style={{ color: '#64748b', fontFamily: 'monospace' }}>{k}:</span>
                                      {' '}
                                      <span style={{ fontWeight: 500 }}>
                                        {resolveSettingValue(pinSettings[k])}
                                      </span>
                                    </div>
                                  ))
                                }
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}

                {/* Neither section has data */}
                {!hasComm && !hasWireless && !hasPins && (
                  <div style={{ color: '#94a3b8', fontSize: 11, fontStyle: 'italic' }}>
                    No configuration data available for this component.
                  </div>
                )}

              </div>
            </div>
          );
        })}
      </div>

      {/* ── Footer ── */}
      <div style={{
        borderTop: '1px solid #e2e8f0',
        padding: '8px 24px',
        background: '#f8fafc',
        display: 'flex',
        justifyContent: 'flex-end',
      }}>
        <span style={{ fontSize: 8, color: '#cbd5e1' }}>
          Generated by ProfiX Labs · prof-ix.com
        </span>
      </div>

    </div>
  );
};

// Shared table cell styles — defined outside component to avoid recreation
const thStyle = {
  padding: '5px 10px',
  textAlign: 'left',
  fontWeight: 700,
  color: '#475569',
  fontSize: 10,
  borderBottom: '1px solid #e2e8f0',
};

const tdStyle = {
  padding: '5px 10px',
  borderBottom: '1px solid #f1f5f9',
  verticalAlign: 'top',
};

// ─── Wire path builder ────────────────────────────────────────────────────────

function buildWirePath(x1, y1, x2, y2) {
  // Orthogonal L-shaped path with a midpoint bend
  const midX = (x1 + x2) / 2;
  return `M ${x1} ${y1} H ${midX} V ${y2} H ${x2}`;
}

// ─── Legend ───────────────────────────────────────────────────────────────────

function Legend({ x, y }) {
  const entries = Object.entries(WIRE_COLORS).filter(([k]) => k !== 'default');
  const colW = 160;
  const cols = 4;

  return (
    <g transform={`translate(${x}, ${y})`}>
      <rect
        x="-10" y="-16"
        width={cols * colW + 20}
        height={Math.ceil(entries.length / cols) * 24 + 24}
        rx="6"
        fill="#f8fafc"
        stroke="#e2e8f0"
        strokeWidth="1"
      />
      <text x="0" y="-2" fontSize="10" fontWeight="700" fill="#475569">
        Wire Legend
      </text>
      {entries.map(([, style], i) => {
        const col = i % cols;
        const row = Math.floor(i / cols);
        const lx = col * colW;
        const ly = row * 24 + 14;
        return (
          <g key={style.label} transform={`translate(${lx}, ${ly})`}>
            <line
              x1="0" y1="6" x2="30" y2="6"
              stroke={style.color}
              strokeWidth="2.5"
              strokeDasharray={style.dash ? '6,3' : '0'}
            />
            <text x="36" y="10" fontSize="9" fill="#475569">
              {style.label}
            </text>
          </g>
        );
      })}
    </g>
  );
}

// ─── Title Block ──────────────────────────────────────────────────────────────

function TitleBlock({ logoSrc, courseTitle, labTitle, circuitName, studentName, circuitId, svgW }) {
  const date = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD
  const BLOCK_H = 72;

  return (
    <g>
      {/* Background */}
      <rect x="0" y="0" width={svgW} height={BLOCK_H} fill="#1e293b" />
      <rect x="0" y={BLOCK_H - 3} width={svgW} height="3" fill="#3b82f6" />

      {/* Logo */}
      {logoSrc && (
        <image
          href={logoSrc}
          x="12" y="8"
          width="52" height="52"
          preserveAspectRatio="xMidYMid meet"
        />
      )}

      {/* Brand name */}
      <text x="72" y="28" fontSize="16" fontWeight="800" fill="#f1f5f9" letterSpacing="0.5">
        ProfiX Labs
      </text>
      <text x="72" y="44" fontSize="9" fill="#94a3b8" letterSpacing="1">
        HARDWARE SCHEMATIC
      </text>

      {/* Center — circuit / course / lab */}
      <text x={svgW / 2} y="22" textAnchor="middle" fontSize="14" fontWeight="700" fill="#f1f5f9">
        {circuitName || 'Circuit Schematic'}
      </text>
      <text x={svgW / 2} y="38" textAnchor="middle" fontSize="10" fill="#94a3b8">
        {courseTitle || ''}
      </text>
      <text x={svgW / 2} y="52" textAnchor="middle" fontSize="10" fill="#64748b">
        {labTitle || ''}
      </text>

      {/* Right — student / date / ID */}
      <text x={svgW - 14} y="22" textAnchor="end" fontSize="10" fill="#94a3b8">
        {studentName || ''}
      </text>
      <text x={svgW - 14} y="36" textAnchor="end" fontSize="9" fill="#64748b">
        {date}
      </text>
      <text x={svgW - 14} y="50" textAnchor="end" fontSize="8" fill="#475569" fontFamily="monospace">
        {circuitId || ''}
      </text>
    </g>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

const TITLE_H   = 80;   // reserved height for title block
const LEGEND_H  = 90;   // reserved height for legend block
const LOGO_PATH = '/images/prof_ix.png'; // same path used in canvasBuilder header

// ADD this utility function near the top of SchematicDiagram.jsx,
// just below the LOGO_PATH constant:

const loadImageAsBase64 = (src) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width  = img.naturalWidth;
      canvas.height = img.naturalHeight;
      const ctx = canvas.getContext('2d');
      ctx.drawImage(img, 0, 0);
      resolve(canvas.toDataURL('image/png'));
    };
    img.onerror = () => resolve(null); // fail gracefully — no logo rather than crash
    img.src = src;
  });
};

const SchematicDiagram = ({
  visible,
  onClose,
  circuit,
  courseInfo,
  labInfo,
  circuitOwnerName,
  initialPage = 'schematic',
}) => {
  const svgRef    = useRef(null);
  const configPageRef = useRef(null);
  const scrollRef       = useRef(null);
  const [exporting, setExporting] = useState(false);
  const [layout, setLayout]       = useState(null);
  const [logoBase64, setLogoBase64] = useState(null); 
  // const [activePage, setActivePage] = useState('schematic');
  const [activePage, setActivePage] = useState(initialPage);
  const [containerW, setContainerW]   = useState(0);

  // Priority: explicit circuitOwnerName → circuit.studentId label → null fallback
  const studentName = circuitOwnerName
    ?? circuit?.studentName        // if your circuit object stores a name
    ?? circuit?.studentId          // fallback to ID if no name stored
    ?? null;             // last resort (viewer's own name)
    
  // Recompute layout whenever circuit changes
  useEffect(() => {
    if (!circuit?.components) return;
    const result = computeLayout(circuit.components);
    setLayout(result);
  }, [circuit]);

  // ADD this useEffect after the layout one:
  useEffect(() => {
      if (!visible) return;
      loadImageAsBase64(LOGO_PATH).then(setLogoBase64);
  }, [visible]);

  useEffect(() => {
    if (!visible) return;
    const el = scrollRef.current;
    if (!el) return;

    const measure = () => setContainerW(el.clientWidth - 48); // 48 = padding (24px each side)
    measure();

    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [visible]);

  useEffect(() => {
    if (visible) setActivePage(initialPage);
  }, [visible, initialPage]);

  // ── Connection rendering ──────────────────────────────────────────────────

  // REPLACE the entire renderConnections function:
  const renderConnections = useCallback(() => {
    if (!layout || !circuit?.connections) return null;

    const hwIds = new Set(layout.placed.map(p => p.instanceId));

    const hwConnections = circuit.connections.filter(conn =>
      hwIds.has(conn.from.componentInstanceId) &&
      hwIds.has(conn.to.componentInstanceId)
    );

    return hwConnections.map((conn, index) => {
      const fromComp = layout.placed.find(p => p.instanceId === conn.from.componentInstanceId);
      const toComp   = layout.placed.find(p => p.instanceId === conn.to.componentInstanceId);
      if (!fromComp || !toComp) return null;

      const style = resolveWireStyleWithIndex(conn, index);

      // ── Identify which end is the board and which is the peripheral ──────
      const fromIsBoard = fromComp.category === 'boards';
      const toIsBoard   = toComp.category   === 'boards';
      const hasBoard    = fromIsBoard || toIsBoard;

      // Board end: show GPIO pin name
      // Peripheral end: show component pin name (SCK, MOSI, SDA etc.)
      const boardPinName      = fromIsBoard ? conn.from.pinName : conn.to.pinName;
      const peripheralPinName = fromIsBoard ? conn.to.pinName   : conn.from.pinName;
      const boardComp         = fromIsBoard ? fromComp : toComp;
      const peripheralComp    = fromIsBoard ? toComp   : fromComp;

      // ── Anchors ───────────────────────────────────────────────────────────
      const fromIsLeft = fromComp.sx < toComp.sx;
      const fromAnchor = getPinAnchor(fromComp, conn.from.pinName, fromIsLeft ? 'right' : 'left');
      const toAnchor   = getPinAnchor(toComp,   conn.to.pinName,   fromIsLeft ? 'left'  : 'right');

      // Board anchor and peripheral anchor regardless of draw direction
      const boardAnchor      = fromIsBoard ? fromAnchor : toAnchor;
      const peripheralAnchor = fromIsBoard ? toAnchor   : fromAnchor;

      // Board is always center — peripheral is always left or right
      const peripheralIsLeft = peripheralComp.sx < boardComp.sx;

      const path = buildWirePath(
        fromAnchor.x, fromAnchor.y,
        toAnchor.x,   toAnchor.y
      );

      return (
        <g key={conn.connectionId}>
          {/* White underline for readability */}
          <path
            d={path}
            fill="none"
            stroke="#ffffff"
            strokeWidth="5"
            strokeLinejoin="round"
          />

          {/* Colored wire */}
          <path
            d={path}
            fill="none"
            stroke={style.color}
            strokeWidth="2.5"
            strokeDasharray={style.dash ? '7,4' : '0'}
            strokeLinejoin="round"
          />

          {hasBoard ? (
            <>
              {/* Peripheral side label — component pin name (SCK, MOSI, SDA...) */}
              <text
                x={peripheralAnchor.x + (peripheralIsLeft ? -6 : 6)}
                y={peripheralAnchor.y - 5}
                fontSize="7"
                fill={style.color}
                fontWeight="600"
                textAnchor={peripheralIsLeft ? 'end' : 'start'}
              >
                {peripheralPinName}
              </text>

              {/* Board side label — "GPIO19 (MISO)" with GPIO in black, pin name in wire color */}
              <text
                x={boardAnchor.x + (peripheralIsLeft ? 6 : -6)}
                y={boardAnchor.y + 4}
                fontSize="7"
                textAnchor={peripheralIsLeft ? 'start' : 'end'}
              >
                <tspan fill="#334155" fontWeight="500">
                  {boardPinName}
                </tspan>
                <tspan fill={style.color} fontWeight="700">
                  {` (${peripheralPinName})`}
                </tspan>
              </text>
            </>
          ) : (
            <>
              {/* No board on either end (e.g. AgenticAI graph edge) — plain
                  independent pin labels at each anchor, no board/peripheral framing */}
              <text
                x={fromAnchor.x}
                y={fromAnchor.y - 5}
                fontSize="7"
                fill={style.color}
                fontWeight="600"
                textAnchor={fromIsLeft ? 'end' : 'start'}
              >
                {conn.from.pinName}
              </text>
              <text
                x={toAnchor.x}
                y={toAnchor.y - 5}
                fontSize="7"
                fill={style.color}
                fontWeight="600"
                textAnchor={fromIsLeft ? 'start' : 'end'}
              >
                {conn.to.pinName}
              </text>
            </>
          )}

        </g>
      );
    });
  }, [layout, circuit?.connections]);

  // ── PDF export ────────────────────────────────────────────────────────────
  const handleExportPDF = useCallback(async () => {
    if (!svgRef.current || !configPageRef.current) return;
    setExporting(true);

    // ── Temporarily force both pages visible for html2canvas ──────────────
    const schematicWrapper = svgRef.current.parentElement;
    const configWrapper    = configPageRef.current.parentElement;
    const prevSchematic    = schematicWrapper.style.display;
    const prevConfig       = configWrapper.style.display;

    schematicWrapper.style.display = 'block';
    configWrapper.style.display    = 'block';

    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => requestAnimationFrame(resolve));

    try {
      let pdf;

      // ── Page 1: Schematic — only when initialPage === 'schematic' ────────
      if (initialPage === 'schematic') {
        const svgEl    = svgRef.current.querySelector('svg');
        const imageEls = svgEl ? Array.from(svgEl.querySelectorAll('image')) : [];

        await Promise.all(
          imageEls.map(async (imgEl) => {
            const href = imgEl.getAttribute('href');
            if (!href || href.startsWith('data:')) return;
            const b64 = await loadImageAsBase64(href);
            if (b64) imgEl.setAttribute('href', b64);
          })
        );

        const canvas1 = await html2canvas(svgRef.current, {
          scale: 2,
          useCORS: true,
          allowTaint: false,
          backgroundColor: '#ffffff',
          logging: false,
        });

        if (canvas1.width === 0 || canvas1.height === 0) {
          throw new Error('Schematic canvas has zero dimensions');
        }

        const imgData1 = canvas1.toDataURL('image/png');
        const mmW1     = (canvas1.width  / 2) / 3.7795;
        const mmH1     = (canvas1.height / 2) / 3.7795;

        pdf = new jsPDF({
          orientation: mmW1 > mmH1 ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [mmW1, mmH1],
        });

        pdf.addImage(imgData1, 'PNG', 0, 0, mmW1, mmH1);
      }

      // ── Page 2 (or only page): Config ─────────────────────────────────────
      const canvas2 = await html2canvas(configPageRef.current, {
        scale: 2,
        useCORS: true,
        allowTaint: false,
        backgroundColor: '#ffffff',
        logging: false,
      });

      if (canvas2.width === 0 || canvas2.height === 0) {
        throw new Error('Config canvas has zero dimensions');
      }

      const imgData2 = canvas2.toDataURL('image/png');
      const mmW2     = (canvas2.width  / 2) / 3.7795;
      const mmH2     = (canvas2.height / 2) / 3.7795;

      if (pdf) {
        // Schematic already created PDF — add config as page 2
        pdf.addPage([mmW2, mmH2], mmW2 > mmH2 ? 'landscape' : 'portrait');
        pdf.addImage(imgData2, 'PNG', 0, 0, mmW2, mmH2);
      } else {
        // Config only — create PDF from scratch
        pdf = new jsPDF({
          orientation: mmW2 > mmH2 ? 'landscape' : 'portrait',
          unit: 'mm',
          format: [mmW2, mmH2],
        });
        pdf.addImage(imgData2, 'PNG', 0, 0, mmW2, mmH2);
      }

      // ── Save ──────────────────────────────────────────────────────────────
      const date       = new Date().toISOString().slice(0, 10);
      const courseSafe = (courseInfo?.title || 'circuit')
        .replace(/\s+/g, '_')
        .replace(/[^a-zA-Z0-9_]/g, '');
      const filename   = `${circuit?.circuitId || 'schematic'}_${courseSafe}_${date}.pdf`;

      pdf.save(filename);

    } catch (err) {
      console.error('❌ PDF export failed:', err);
      console.error('❌ Error message:', err.message);
      alert(`PDF export failed: ${err.message}`);
    } finally {
      schematicWrapper.style.display = prevSchematic;
      configWrapper.style.display    = prevConfig;
      setExporting(false);
    }
  }, [circuit, courseInfo, initialPage]);

  // ── Guard ─────────────────────────────────────────────────────────────────

  if (!visible || !circuit) return null;

  const { placed = [], svgW: layoutW = 800, svgH = 600 } = layout || {};
  const svgW      = Math.max(layoutW, containerW || layoutW); // fill container
  const totalSvgH = TITLE_H + svgH + LEGEND_H;

  return (
    <div className="schematic-overlay" onClick={onClose}>
      <div
        className="schematic-modal"
        onClick={e => e.stopPropagation()}
      >
        {/* Modal header */}
        <div className="schematic-modal-header">    
          <h2 className="schematic-modal-title">📐 Circuit Schematic</h2>

          {/* Page tabs */}
          <div className="schematic-tabs">
            {initialPage === 'schematic' && (
              <button
                className={`schematic-tab ${activePage === 'schematic' ? 'active' : ''}`}
                onClick={() => setActivePage('schematic')}
              >
                📐 Schematic
              </button>
            )}
            <button
              className={`schematic-tab ${activePage === 'config' ? 'active' : ''}`}
              onClick={() => setActivePage('config')}
            >
              ⚙️ Configuration
            </button>
          </div>

          <div className="schematic-modal-actions">
            <button
              className="schematic-export-btn"
              onClick={handleExportPDF}
              disabled={exporting || !layout}
            >
              {exporting ? '⏳ Generating PDF...' : '📄 Export PDF'}
            </button>
            <button className="schematic-close-btn" onClick={onClose}>✖</button>
          </div>
        </div>

        {/* SVG preview — this is what html2canvas captures */}
        <div className="schematic-preview-scroll" ref={scrollRef}>
          {/* Page 1 — Schematic */}
          <div style={{ display: activePage === 'schematic' ? 'block' : 'none' }}>
            <div ref={svgRef} className="schematic-svg-wrapper">
              <svg
                xmlns="http://www.w3.org/2000/svg"
                width={svgW}
                height={totalSvgH}
                style={{ background: '#ffffff', display: 'block' }}
              >
                {/* ── Title block ── */}
                <TitleBlock
                    logoSrc={logoBase64 || LOGO_PATH}
                    courseTitle={courseInfo?.title}
                    labTitle={labInfo?.title}
                    circuitName={circuit?.name}
                    studentName={studentName}
                    circuitId={circuit?.circuitId}
                    svgW={svgW}
                />

                {/* ── Diagram area ── */}
                <g transform={`translate(0, ${TITLE_H})`}>
                  {/* Grid dots — subtle background grid */}
                  <defs>
                    <pattern id="grid" width="20" height="20" patternUnits="userSpaceOnUse">
                      <circle cx="1" cy="1" r="0.8" fill="#e2e8f0" />
                    </pattern>
                  </defs>
                  <rect width={svgW} height={svgH} fill="url(#grid)" />

                  {/* Connections drawn first so they appear under boxes */}
                  {renderConnections()}

                  {/* Component boxes */}
                  {placed.map(comp => (
                    <ComponentBox key={comp.instanceId} comp={comp} />
                  ))}

                  {/* Empty state */}
                  {placed.length === 0 && (
                    <text
                      x={svgW / 2} y={svgH / 2}
                      textAnchor="middle"
                      fontSize="14"
                      fill="#94a3b8"
                    >
                      No hardware components found in this circuit.
                    </text>
                  )}
                </g>

                {/* ── Legend ── */}
                <g transform={`translate(0, ${TITLE_H + svgH})`}>
                  <rect width={svgW} height={LEGEND_H} fill="#f8fafc" />
                  <rect width={svgW} height="2" fill="#e2e8f0" />
                  <Legend x={CANVAS_PAD} y={20} />
                  {/* Footer line */}
                  <text
                    x={svgW - 12} y={LEGEND_H - 8}
                    textAnchor="end"
                    fontSize="8"
                    fill="#cbd5e1"
                  >
                    Generated by ProfiX Labs · prof-ix.com
                  </text>
                </g>
              </svg>
            </div>
          </div>

          {/* ── Page 2: Config page — rendered below schematic, captured separately ── */}
          {/* <div style={{ marginTop: 32 }}> */}
          <div style={{ display: activePage === 'config' ? 'block' : 'none' }}>
            <div ref={configPageRef} style={{ minWidth: layout?.svgW || 800 }}>
              <ConfigPage
                components={circuit?.components || []}
                logoSrc={logoBase64 || LOGO_PATH}
                courseTitle={courseInfo?.title}
                labTitle={labInfo?.title}
                circuitName={circuit?.name}
                studentName={studentName}
                circuitId={circuit?.circuitId}
                svgW={svgW}
              />
            </div>
          </div>
          {/* </div> */}
        </div>   
      </div>     
    </div>       
  );
};

export default SchematicDiagram;